import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const PRESETS = [
  { key: "card", labelKey: "Card", w: 360, h: 480 },
  { key: "banner", labelKey: "Banner", w: 728, h: 240 },
  { key: "full", labelKey: "Full", w: 1200, h: 720 },
];

// Attribute-escape anything interpolated into the snippet: the EPK title is
// owner-controlled and a bare " would break out of title="..." in the copied code.
export const escapeAttr = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// the snippet points at the existing public /epk/:id page rather than a
// dedicated scaled embed endpoint - that endpoint doesn't exist. Add one if a real
// third-party embed (not just "copy code, paste later") becomes a real ask.
export default function EmbedWidgetPicker({ epkUrl, title }) {
  const { t } = useTranslation();
  const [preset, setPreset] = useState("card");
  const [copied, setCopied] = useState(false);

  const active = PRESETS.find((p) => p.key === preset);
  const snippet = `<iframe src="${escapeAttr(epkUrl)}" width="${active.w}" height="${active.h}" style="border:0;border-radius:8px" loading="lazy" title="${escapeAttr(title || "EPK")}"></iframe>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(snippet)
      .then(() => {
        setCopied(true);
        toast.success(t("Embed code copied!"));
        setTimeout(() => setCopied(false), 1600);
      })
      .catch(() => toast.error(t("Failed to copy code")));
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2.5">
      <div className="tw-flex tw-items-baseline tw-justify-between">
        <span className="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-[#1e0039]/70">
          {t("Embed widget")}
        </span>
      </div>

      <div className="tw-flex tw-gap-1.5">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPreset(p.key)}
            className={`tw-flex-1 tw-flex tw-flex-col tw-items-start tw-gap-0.5 tw-rounded-lg tw-px-2 tw-py-1.5 tw-text-left tw-cursor-pointer ${
              preset === p.key
                ? "tw-bg-[#FF43A7]/15 tw-border tw-border-[#FF43A7] tw-text-[#FF43A7]"
                : "tw-bg-transparent tw-border tw-border-[#AA8894]/30 tw-text-[#1e0039]/60"
            }`}
          >
            <span className="tw-text-[10px] tw-font-extrabold tw-uppercase tw-tracking-wide">{t(p.labelKey)}</span>
            <span className="tw-text-[8.5px] tw-tracking-wide tw-opacity-70">{p.w}×{p.h}</span>
          </button>
        ))}
      </div>

      <div className="tw-flex tw-min-h-[100px] tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-border-dashed tw-border-[#FF43A7]/40 tw-bg-[#FF43A7]/5 tw-p-3 tw-text-center">
        <span className="tw-text-[9px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-[#1e0039]/50">
          {t("widget preview")}
        </span>
        <span className="tw-text-[9px] tw-text-[#1e0039]/40">{active.w} × {active.h}</span>
      </div>

      <button
        type="button"
        onClick={handleCopy}
        className="tw-h-9 tw-rounded-lg tw-bg-[#FF43A7] tw-text-[10px] tw-font-extrabold tw-uppercase tw-tracking-wide tw-text-white tw-border-none tw-cursor-pointer"
      >
        {copied ? `✓ ${t("Copied")}` : t("Copy embed code")}
      </button>
    </div>
  );
}
